import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Plus, StickyNote, Upload, X} from 'lucide-react';
import {useClickOutside} from '@/matic/lib/hooks/useClickOutside';
import {useUiMode} from '@/matic/features/_shared/hooks/useUiMode';
import {useOverlayPanelClaim} from '@/matic/features/_shared/hooks/useActiveOverlayPanel';
import {useObjectTypes} from '@/matic/features/master-data/hooks/useObjectTypes';
import {useFileTags} from '@/matic/features/files/hooks/useFileTags';
import {OverlayPanelLayout} from '@/matic/features/_shared/components/layout/OverlayPanelLayout';
import {useFilePermissions} from '@/matic/features/files/hooks/useFilePermissions';
import {useQuickDropUpload} from '@/matic/features/files/hooks/useQuickDropUpload';
import {QuickDropTray} from '@/matic/features/files/components/QuickDropTray';
import {ManagedFilePreviewPanel} from '@/matic/features/files/components/ManagedFilePreviewPanel';
import type {ManagedFile} from '@/matic/features/files/types/files-types';
import {useLibraryScopeContextAssociations} from '@/matic/features/relay/hooks/useLibraryScopeContextAssociations';
import {RelayCreateDialog} from '@/matic/features/relay/components/library/RelayCreateDialog';

type ActionCardProps = {
  icon: React.ComponentType<{className?: string}>;
  label: string;
  description: string;
  onClick: () => void;
};

/** One clickable card in the FAB's popover. */
function ActionCard({
  icon: Icon,
  label,
  description,
  onClick,
}: ActionCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-neutral-900">
          {label}
        </span>
        <span className="block truncate text-xs text-neutral-500">
          {description}
        </span>
      </span>
    </button>
  );
}

/**
 * The "View" action's target — a right-edge slide-in drawer, the same shape
 * a file takes everywhere else in the app (Otto's RHS, Library's overlay
 * panel), so a file just uploaded through the FAB previews the same way
 * instead of bouncing out to a raw S3 URL in a new tab. Reuses
 * `OverlayPanelLayout` (same component every other file viewer uses) so it
 * gets resize-drag and slide transitions for free, and `ManagedFilePreviewPanel`
 * (same wired-up viewer the Files browser/Chat-tab citations use) so this
 * gets Delete/rename/recategorize/associations for free too — not just a
 * bare preview. Wrapped in its own stacking context (`z-50`) so it renders
 * above the FAB's other floating elements (button, popover, tray), which
 * live at `z-40` in the same portal.
 */
function FileViewDrawer({
  file,
  onClose,
}: {
  file: ManagedFile | undefined;
  onClose: () => void;
}) {
  useOverlayPanelClaim('global-file-view-drawer', Boolean(file), onClose);

  return (
    <div className="relative z-50">
      <OverlayPanelLayout
        panel={
          file ? (
            <ManagedFilePreviewPanel file={file} onClose={onClose} />
          ) : undefined
        }
      >
        {() => null}
      </OverlayPanelLayout>
    </div>
  );
}

/**
 * The single global "throw it over the fence" entry point - a fixed
 * bottom-right FAB present on every screen (Otto/Relay mode). Clicking it
 * slides up a small popover with exactly two choices - Upload files / New
 * note. "Upload files" goes straight to the OS file picker (no dropzone
 * modal in between - that was one extra click with no purpose, since
 * drag-and-drop already works anywhere on the page without opening
 * anything). "New note" opens its own focused dialog. Also owns:
 *  - Window-level drag-and-drop: dropping a file ANYWHERE on the page starts
 *    a quick-drop upload immediately, no popover required.
 *  - The filing tray, so drops from either path (drag-anywhere or the file
 *    picker) report through one shared instance.
 *  - The file-view drawer, so "View" pops a file open in-app instead of a
 *    raw storage URL in a new tab.
 *
 * Rendered from both SidebarNav (expanded) and CollapsedSidebar - they are
 * mutually exclusive (only one sidebar variant is mounted at a time), so
 * mounting this from both is safe and keeps each sidebar file simple.
 * Portal'd to document.body: the sidebar's own DOM subtree only spans the
 * left column, but drag events and the FAB need to reach/cover the whole
 * viewport.
 */
export function GlobalCreateFab() {
  const {mode: uiMode} = useUiMode();
  const {canManage: canManageFiles} = useFilePermissions();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState<ManagedFile | undefined>();
  const popoverRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const closeFileView = useCallback(() => {
    setViewingFile(undefined);
  }, []);
  useClickOutside(
    popoverRef,
    () => {
      setPopoverOpen(false);
    },
    popoverOpen
  );

  const {data: objectTypesData} = useObjectTypes();
  const objectTypes = objectTypesData?.items ?? [];
  const contextAssociations = useLibraryScopeContextAssociations();
  const {data: categoriesData} = useFileTags();
  const categoryNameById = useMemo(
    () => new Map((categoriesData ?? []).map((c) => [c.id, c.name])),
    [categoriesData]
  );

  // The dialog/drag path can only carry ONE association today (quick-drop's
  // "throw it over the fence" story is single-context: whichever record page
  // you are on). Multi-scope Library selections just use the first. Quick-drop
  // doesn't support type-only (Category) associations, so a Category-scoped
  // primary is treated as unassociated rather than passed through.
  const primaryAssociation = contextAssociations[0];
  const quickDrop = useQuickDropUpload({
    association:
      primaryAssociation && primaryAssociation.objectRecordId !== null
        ? {
            objectTypeId: primaryAssociation.objectTypeId,
            objectRecordId: primaryAssociation.objectRecordId,
            label: primaryAssociation.objectRecordLabel,
          }
        : undefined,
    categoryNameById,
  });

  // Depth counter: dragenter/dragleave fire per element crossed, so a
  // boolean flickers - track depth at the document level instead.
  const dragDepthRef = useRef(0);
  const [isDragActive, setIsDragActive] = useState(false);

  useEffect(() => {
    if (uiMode !== 'otto' || !canManageFiles) return;

    const hasFiles = (e: DragEvent) =>
      [...(e.dataTransfer?.types ?? [])].includes('Files');

    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current += 1;
      setIsDragActive(true);
    };

    const onDragOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
    };

    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setIsDragActive(false);
    };

    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current = 0;
      setIsDragActive(false);
      quickDrop.dropFiles([...(e.dataTransfer?.files ?? [])]);
    };

    globalThis.addEventListener('dragenter', onDragEnter);
    globalThis.addEventListener('dragover', onDragOver);
    globalThis.addEventListener('dragleave', onDragLeave);
    globalThis.addEventListener('drop', onDrop);
    return () => {
      globalThis.removeEventListener('dragenter', onDragEnter);
      globalThis.removeEventListener('dragover', onDragOver);
      globalThis.removeEventListener('dragleave', onDragLeave);
      globalThis.removeEventListener('drop', onDrop);
    };
  }, [uiMode, canManageFiles, quickDrop]);

  if (uiMode !== 'otto') return null;

  return createPortal(
    <>
      {canManageFiles && isDragActive && (
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 p-8">
          <div className="flex flex-col items-center gap-3 rounded-xl border-2 border-dashed border-white/80 bg-neutral-900/30 px-12 py-10 text-center">
            <Upload className="h-8 w-8 text-white" />
            <p className="text-lg font-semibold text-white">
              Drop to add to MATIC
            </p>
            <p className="text-sm text-white/80">
              {primaryAssociation
                ? `Files will be classified and linked to ${primaryAssociation.objectRecordLabel}`
                : "Files will be classified and filed - we'll sort out the rest"}
            </p>
          </div>
        </div>
      )}

      <QuickDropTray
        items={quickDrop.items}
        onDismiss={quickDrop.dismissItem}
        onClear={quickDrop.clearItems}
        onView={(file) => {
          setViewingFile(file);
        }}
      />

      {popoverOpen && (
        <div
          ref={popoverRef}
          className="fixed bottom-24 right-6 z-40 w-64 origin-bottom-right animate-popover-slide-up space-y-2"
        >
          {canManageFiles && (
            <ActionCard
              icon={Upload}
              label="Upload files"
              description="Pick from your desktop"
              onClick={() => {
                setPopoverOpen(false);
                fileInputRef.current?.click();
              }}
            />
          )}
          <ActionCard
            icon={StickyNote}
            label="New note"
            description="Jot something down"
            onClick={() => {
              setPopoverOpen(false);
              setNoteOpen(true);
            }}
          />
        </div>
      )}

      <button
        type="button"
        aria-label={popoverOpen ? 'Close create menu' : 'Create'}
        title="Create"
        onClick={() => {
          setPopoverOpen((open) => !open);
        }}
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-sidebar-bg text-white shadow-lg ring-1 ring-black/10 transition-transform hover:scale-105 hover:bg-sidebar-hover active:scale-95"
      >
        {popoverOpen ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
      </button>

      {canManageFiles && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            quickDrop.dropFiles([...(e.target.files ?? [])]);
            e.target.value = '';
          }}
        />
      )}

      <RelayCreateDialog
        open={noteOpen}
        onClose={() => {
          setNoteOpen(false);
        }}
        types={objectTypes}
        contextAssociations={contextAssociations}
        onQuickDrop={quickDrop.dropFiles}
        initialStep="note"
      />

      <FileViewDrawer file={viewingFile} onClose={closeFileView} />
    </>,
    document.body
  );
}
